import type { IStockRepository } from '@/features/stock/domain/repositories/IStockRepository.js';
import type { StockTransactionEntity } from '@/features/stock/domain/entities/StockEntity.js';

interface Deps { stockRepository: IStockRepository; }

export interface AcceptTransactionParams {
  id: string;
  items: Array<{
    id?: string;
    assetId: string;
    /** Batch this line draws from — must survive to consume the right batch. */
    sourceBatchId?: string | null;
    batchCodeId?: string | null;
    /** Quantity originally requested (booked). */
    unitQty: number;
    /** Quantity the approver actually received. */
    confirmedUnitQty: number;
    shrinkageReason?: string | null;
  }>;
  /** Signature/proof photo URLs. Required (>=1) for transfer. */
  photos?: string[];
}

export function createAcceptTransactionUseCase({ stockRepository }: Deps) {
  return async function acceptTransaction(params: AcceptTransactionParams): Promise<StockTransactionEntity | null> {
    return stockRepository.acceptTransaction({
      id: params.id,
      photos: params.photos ?? [],
      items: params.items.map((it) => {
        if (!Number.isSafeInteger(it.unitQty) || it.unitQty <= 0) throw new Error('Số lượng yêu cầu phải là số nguyên dương');
        if (!Number.isSafeInteger(it.confirmedUnitQty) || it.confirmedUnitQty < 0 || it.confirmedUnitQty > it.unitQty) throw new Error('Thực nhận phải là số nguyên từ 0 đến số lượng yêu cầu');
        const confirmed = it.confirmedUnitQty;
        const shrinkage = Math.max(0, it.unitQty - confirmed);
        return {
          ...(it.id !== undefined ? { id: it.id } : {}),
          assetId: it.assetId,
          sourceBatchId: it.sourceBatchId ?? null,
          batchCodeId: it.batchCodeId ?? null,
          unitQty: it.unitQty,
          confirmedUnitQty: confirmed,
          shrinkageUnitQty: shrinkage,
          shrinkageReason: shrinkage > 0 ? (it.shrinkageReason ?? null) : null,
        };
      }),
    });
  };
}
